// Detalhe de uma configuração — só leitura (imutável). Recebe tudo por props;
// quem busca e muta é a página.
import {
  BanknoteIcon,
  CalendarClockIcon,
  PercentIcon,
  StoreIcon,
} from "lucide-react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { CopyableValue } from "@/components/commons/copyable-value"
import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import { Skeleton } from "@/components/ui/skeleton"
import type { PaymentConfiguration } from "@/types/payment-configurations"

import { formatDateTime } from "../utils"
import { ChargeRuleText } from "./charge-rule-text"
import { ImmutabilityNotice } from "./immutability-notice"
import { PaymentConfigurationStatusChip } from "./payment-configuration-status-chip"

const prefix = "pages.paymentConfigurations"

interface PaymentConfigurationDetailProps {
  configuration: PaymentConfiguration
  whiteLabelName: string
}

// Campos por seção no detalhe real (visão geral e vencimento: 5 cada).
const DETAIL_FIELDS_PER_SECTION = 5

/** Casca do `SectionCard`: barra de título (ícone + texto) e o corpo. */
const SectionCardSkeleton = ({
  children,
}: {
  children: ReactNode
}) => (
  <div
    className="rounded-lg border border-border bg-card"
    data-testid="section-card-skeleton"
  >
    <div className="flex items-center gap-2 border-b border-border bg-muted/50 px-4 py-3">
      <Skeleton className="size-4 rounded" />
      <Skeleton className="h-4 w-32" />
    </div>
    <div className="p-4">{children}</div>
  </div>
)

/** Grade label/valor no mesmo grid do `InfoItem` real. */
const InfoGridSkeleton = () => (
  <div className="grid gap-4 @2xl:grid-cols-3">
    {Array.from({ length: DETAIL_FIELDS_PER_SECTION }).map(
      (_, index) => (
        <div
          key={index}
          className="flex flex-col gap-1.5"
          data-testid="info-item-skeleton"
        >
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      )
    )}
  </div>
)

export const PaymentConfigurationDetailSkeleton = () => (
  <div
    className="space-y-6"
    data-testid="payment-configuration-detail-skeleton"
    aria-hidden="true"
  >
    {/* Aviso de imutabilidade */}
    <Skeleton className="h-[4.5rem] w-full rounded-xl" />
    <SectionCardSkeleton>
      <InfoGridSkeleton />
    </SectionCardSkeleton>
    <SectionCardSkeleton>
      <InfoGridSkeleton />
    </SectionCardSkeleton>
    {/* Multa e juros lado a lado */}
    <div className="grid gap-6 @2xl:grid-cols-2">
      <SectionCardSkeleton>
        <Skeleton className="h-4 w-2/3" />
      </SectionCardSkeleton>
      <SectionCardSkeleton>
        <Skeleton className="h-4 w-2/3" />
      </SectionCardSkeleton>
    </div>
  </div>
)

export const PaymentConfigurationDetail = ({
  configuration,
  whiteLabelName,
}: PaymentConfigurationDetailProps) => {
  const { t, i18n } = useTranslation()
  const daysText = (days?: number) =>
    days === undefined
      ? t("common.notAvailable")
      : t(`${prefix}.rules.days`, { count: days })

  return (
    <div className="space-y-6">
      <ImmutabilityNotice />

      <SectionCard
        title={t(`${prefix}.detail.overview`)}
        icon={
          <StoreIcon
            className="size-4"
            aria-hidden="true"
          />
        }
      >
        <div className="grid gap-4 @2xl:grid-cols-3">
          <InfoItem label={t(`${prefix}.fields.id`)}>
            <CopyableValue
              value={configuration.id}
              label={t(`${prefix}.fields.id`)}
            />
          </InfoItem>
          <InfoItem label={t(`${prefix}.fields.status`)}>
            <PaymentConfigurationStatusChip
              active={configuration.active}
            />
          </InfoItem>
          <InfoItem
            label={t(`${prefix}.fields.whiteLabel`)}
            value={whiteLabelName}
          />
          <InfoItem
            label={t(`${prefix}.fields.createdAt`)}
            value={formatDateTime(
              configuration.createdAt,
              i18n.language
            )}
          />
          <InfoItem
            label={t(`${prefix}.fields.updatedAt`)}
            value={formatDateTime(
              configuration.updatedAt,
              i18n.language
            )}
          />
        </div>
      </SectionCard>

      <SectionCard
        title={t(`${prefix}.form.sections.due`)}
        icon={
          <CalendarClockIcon
            className="size-4"
            aria-hidden="true"
          />
        }
      >
        <div className="grid gap-4 tabular-nums @2xl:grid-cols-3">
          <InfoItem
            label={t(`${prefix}.fields.daysDue`)}
            value={daysText(configuration.daysDue ?? 0)}
          />
          <InfoItem
            label={t(`${prefix}.fields.daysAfterDue`)}
            value={daysText(configuration.daysAfterDue)}
          />
          <InfoItem
            label={t(`${prefix}.fields.preChargeDays`)}
            value={daysText(configuration.preChargeDays)}
          />
          <InfoItem
            label={t(
              `${prefix}.fields.pixAutoPreChargeDays`
            )}
            value={daysText(
              configuration.pixAutoPreChargeDays
            )}
          />
          <InfoItem
            label={t(
              `${prefix}.fields.generateAllInvoices`
            )}
            value={
              configuration.generateAllInvoices
                ? t("common.yes")
                : t("common.no")
            }
          />
        </div>
      </SectionCard>

      <div className="grid gap-6 @2xl:grid-cols-2">
        <SectionCard
          title={t(`${prefix}.fields.fine`)}
          icon={
            <BanknoteIcon
              className="size-4"
              aria-hidden="true"
            />
          }
        >
          <p className="text-sm">
            <ChargeRuleText charge={configuration.fine} />
          </p>
        </SectionCard>
        <SectionCard
          title={t(`${prefix}.fields.interest`)}
          icon={
            <PercentIcon
              className="size-4"
              aria-hidden="true"
            />
          }
        >
          <p className="text-sm">
            <ChargeRuleText
              charge={configuration.interest}
            />
          </p>
        </SectionCard>
      </div>
    </div>
  )
}
